import { useEffect, useRef } from "react";
import withName from "../../hoc/withName";
import injectScript from "../../utils/inject";

const placeSchema = {
    locality: {
        access: ["long_name"],
        name: "city"
    },
    administrative_area_level_1: {
        access: ["short_name"],
        name: "region"
    },
    country: {
        access: ["long_name", "short_name"],
        name: "country"
    }
};

const createPlaceObject = async (autocomplete, onChange) => {
    // Get the place details from the autocomplete object.
    const place = autocomplete.current && autocomplete.current.getPlace();

    // If place is undefined or doesn't have address_components, user typed text without selecting
    if (!place || !place.address_components) {
        return; // Don't call onChange, validation will catch missing fields
    }

    const formatedPlace = {};

    // Get each component of the address from the place details,
    // and then fill-in the corresponding field on the form.
    for (const component of place.address_components) {
        const addressType = component.types[0];

        if (placeSchema[addressType]) {
            let val;
            if (placeSchema[addressType].access.length === 1) {
                val = component[placeSchema[addressType].access[0]];
            } else {
                val = placeSchema[addressType].access.reduce((res, a) => {
                    res[a] = component[a];
                    return res;
                }, {});
            }
            formatedPlace[placeSchema[addressType].name] = val;
        }
    }

    onChange(formatedPlace);
};

const GooglePlacesAutocomplete = ({
    apiKey = "",
    language = "",
    placeholder = "",
    defaultValue = "",
    onChange = console.log,
    onLoadFailed = console.error,
    onInvalidInput = null,
    inputId = "autocomplete"
}) => {
    const autocomplete = useRef(null);
    const inputRef = useRef(null);
    const placeSelected = useRef(false);

    const init = () => {
        if (!window.google) throw new Error("[react-google-places-autocomplete]: Google script not loaded");
        if (!window.google.maps) throw new Error("[react-google-places-autocomplete]: Google maps script not loaded");
        if (!window.google.maps.places) throw new Error("[react-google-places-autocomplete]: Google maps places script not loaded");

        const input = document.getElementById(inputId);
        if (!input) return;

        // Set initial value
        if (defaultValue) {
            input.value = defaultValue;
        }

        autocomplete.current = new window.google.maps.places.Autocomplete(
            input,
            { types: ["country", "locality", "administrative_area_level_3"] }
        );
        autocomplete.current.setFields(["address_components"]);
        autocomplete.current.addListener("place_changed", () => {
            const place = autocomplete.current && autocomplete.current.getPlace();
            if (place && place.address_components) {
                placeSelected.current = true;
                createPlaceObject(autocomplete, onChange);
            }
        });

        // Detect when user types without selecting
        input.addEventListener("blur", () => {
            if (!placeSelected.current && input.value && onInvalidInput) {
                onInvalidInput();
            }
        });

        input.addEventListener("input", () => {
            placeSelected.current = false;
        });
    };

    useEffect(() => {
        const initCall = async () => {
            try {
                if (apiKey) await injectScript(apiKey, language ? language : "en");
                init();
            } catch (error) {
                onLoadFailed(error);
            }
        };

        initCall();
    }, []);
    return (
        <div id="locationField">
            <input
                ref={inputRef}
                id={inputId}
                className="form-control new font13 text-center text-md-left"
                placeholder={placeholder}
                type="text"
                autoComplete="off"
                onChange={() => {placeSelected.current = false;onInvalidInput();}}
            />
        </div>
    );
};

export default withName(GooglePlacesAutocomplete, "PlacesAuto");
